import { REDUCED_MOTION_QUERY } from "../../../shared/hooks/usePrefersReducedMotion";
import "./form-shake.scss";

/**
 * "Submit shakes the invalid fields": when a form is sent with fields that fail validation, each
 * invalid field's control shakes once and the first one takes focus. No error line is added —
 * the form's own error rendering stays as it is.
 *
 * Two ways in:
 * - `shakeInvalidFields(root)` — call it where a submit is known to have failed
 *   (`KuikaFormPage` does, from `onFinishFailed`; so do forms that submit through `form.submit()`,
 *   which dispatches no DOM event).
 * - `installFormShake()` — one capture listener on `document` that catches every DOM submit:
 *   an antd `htmlType="submit"` button, a `KModalFooter` button bound through `submitForm`, Enter
 *   in a field. Installed once at each entry point.
 */

/** Plays `kuika-shake` once on the control it is put on (form-shake.scss). */
export const FORM_SHAKE_CLASS: string = "kuika-form-shake";

/**
 * On a <form> whose owner calls `shakeInvalidFields` itself (`KuikaFormPage`), so the document
 * listener leaves it alone and one failed submit shakes once, not twice.
 */
export const SHAKES_ITSELF_ATTRIBUTE: string = "data-kuika-shakes-itself";

// An invalid field in each of the form systems the designer uses: the kit input's own flag, a
// `KuikaFormField` row, an antd `Form.Item`.
const INVALID_FIELD_SELECTOR: string = ".kuika-input--error, .kuika-form-field--error, .ant-form-item-has-error";

// What moves inside an invalid field: the kit input's frame, antd's control box. A field with
// neither (a switch in a form row) moves whole.
const CONTROL_SELECTOR: string = ".kuika-input, .ant-form-item-control-input";

const FOCUSABLE_SELECTOR: string = [
  'input:not([type="hidden"]):not(:disabled)',
  "textarea:not(:disabled)",
  "select:not(:disabled)",
  "button:not(:disabled)",
  '[tabindex]:not([tabindex="-1"])'
].join(", ");

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === "function" && window.matchMedia(REDUCED_MOTION_QUERY).matches;

const controlOf = (field: Element): Element => {
  if (field.matches(".kuika-input")) {
    return field;
  }
  return field.querySelector(CONTROL_SELECTOR) ?? field;
};

/**
 * The controls to shake, in document order. Nested hits collapse to the outermost: an antd item's
 * control box that holds a kit input moves the input with it, and shaking both would double it.
 */
const findShakeTargets = (root: HTMLElement): Element[] => {
  const controls: Set<Element> = new Set<Element>();
  root.querySelectorAll(INVALID_FIELD_SELECTOR).forEach((field: Element): void => {
    controls.add(controlOf(field));
  });

  const all: Element[] = Array.from(controls);
  return all.filter(
    (control: Element): boolean => !all.some((other: Element): boolean => other !== control && other.contains(control))
  );
};

const focusFirst = (target: Element): void => {
  const focusable: HTMLElement | null = target.matches(FOCUSABLE_SELECTOR)
    ? (target as HTMLElement)
    : target.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
  focusable?.focus();
};

const shake = (target: Element): void => {
  const settle = (event: Event): void => {
    // A control's own children animate too (a KuikaSwap label, a spinner); only its shake ends it.
    if (event.target !== target) {
      return;
    }
    target.classList.remove(FORM_SHAKE_CLASS);
    target.removeEventListener("animationend", settle);
    target.removeEventListener("animationcancel", settle);
  };

  // Restarted rather than skipped when it is already on: a second submit while the first shake
  // is still playing shakes again, and a class left behind by a shake that never ran (the field
  // was hidden) can never block the next one.
  target.classList.remove(FORM_SHAKE_CLASS);
  void (target as HTMLElement).offsetWidth;
  target.classList.add(FORM_SHAKE_CLASS);
  target.addEventListener("animationend", settle);
  target.addEventListener("animationcancel", settle);
};

/**
 * Shakes every invalid field's control inside `root` once and focuses the first. Under reduced
 * motion nothing moves; the first invalid field still takes focus. A root with no invalid field is
 * left untouched, focus included.
 */
export const shakeInvalidFields = (root: HTMLElement): void => {
  const targets: Element[] = findShakeTargets(root);
  if (targets.length === 0) {
    return;
  }

  if (!prefersReducedMotion()) {
    targets.forEach(shake);
  }
  focusFirst(targets[0]);
};

let uninstallFormShake: (() => void) | null = null;

/**
 * Listens for every DOM submit in the document and, once validation has painted, shakes the
 * submitted form's invalid fields. Returns the uninstaller; a second call while installed returns
 * the same one instead of adding a second listener.
 */
export const installFormShake = (): (() => void) => {
  if (uninstallFormShake) {
    return uninstallFormShake;
  }

  const handleSubmit = (event: Event): void => {
    const form: EventTarget | null = event.target;
    if (!(form instanceof HTMLFormElement) || form.hasAttribute(SHAKES_ITSELF_ATTRIBUTE)) {
      return;
    }
    // Validation settles after the submit event: rc-field-form resolves its rules in promises and
    // antd paints `-has-error` on the render that follows (a frame later for `noStyle` items).
    // Two frames cover both. A form the submit closed is gone by then.
    requestAnimationFrame((): void => {
      requestAnimationFrame((): void => {
        if (form.isConnected) {
          shakeInvalidFields(form);
        }
      });
    });
  };

  // Capture: rc-field-form's `onSubmit` stops propagation, so a bubbling listener never hears it.
  document.addEventListener("submit", handleSubmit, true);
  uninstallFormShake = (): void => {
    document.removeEventListener("submit", handleSubmit, true);
    uninstallFormShake = null;
  };
  return uninstallFormShake;
};
